interface GTMConfig {
  containerIds: string[];
  enableGTM: boolean;
}

type GTMDataLayer = {
  dataLayer: ({
    event: string;
  } & Record<string, unknown>)[];
};

interface User {
  userId: string;
  userRole: 'OWNER' | 'TECHNICIAN' | 'AIRSHIP_ADMIN';
  organizationId: string;
  email: string;
}

const hasDataLayer = (win: Window): win is Window & GTMDataLayer =>
  Boolean('dataLayer' in win && Array.isArray(win.dataLayer));

const buildDataLayer = (win: Window) =>
  hasDataLayer(win)
    ? win
    : Object.assign<Window, GTMDataLayer>(win, {
        dataLayer: [],
      });

const setupGTM = (config: GTMConfig): void => {
  const containerId = config.containerIds.flatMap((id) => id || []).at(0);
  if (!config.enableGTM || !containerId) {
    return;
  }

  const airshipWindow = buildDataLayer(window);
  if (airshipWindow.dataLayer.some((item) => item.event === 'gtm.js')) {
    return;
  }
  airshipWindow.dataLayer.push({
    'gtm.start': new Date().getTime(),
    event: 'gtm.js',
  });

  const scriptElement = document.createElement('script');
  scriptElement.async = true;
  scriptElement.src = `https://www.googletagmanager.com/gtm.js?id=${containerId}`;

  const firstScript = document.getElementsByTagName('script').item(0);
  if (firstScript && firstScript.parentNode) {
    firstScript.parentNode.insertBefore(scriptElement, firstScript);
  }
};

const setGTMUser = (user: User) =>
  buildDataLayer(window).dataLayer.push({
    event: 'user_context_set',
    userId: user.userId,
    userRole: user.userRole,
    organizationId: user.organizationId,
    email: user.email,
  });

const pushGTMEvent = (eventName: string, eventData?: Record<string, unknown>) =>
  buildDataLayer(window).dataLayer.push({ event: eventName, ...eventData });

export { pushGTMEvent, setGTMUser, setupGTM };
